import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { OTHER_SPORTS } from '../constants/positions';
import { CoachProfileShell } from '../components/ProfileShell';
import Button from '../components/Button';
import CollegePicker from '../components/CollegePicker';

export default function CoachAcademicsPage() {
  const { user, API_URL } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const COACH_SPORTS = ['Football', ...OTHER_SPORTS.filter(s => s !== 'Flag Football')];

  const [profile, setProfile] = useState({
    school_name: '',
    school_logo: null,
    position_title: '',
    sport: 'Football',
    division: '',
    state: '',
  });

  // College autocomplete state
  const [collegesData, setCollegesData] = useState([]);
  const [collegeQuery, setCollegeQuery] = useState('');

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchProfile();
    fetch('/data/colleges.json')
      .then(r => r.json())
      .then(data => setCollegesData(data))
      .catch(err => console.error('Error loading colleges data:', err));
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/coaches/profile`, getAxiosConfig());
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data }));
        setCollegeQuery(response.data.school_name || '');
      }
    } catch (error) {
      console.log('No existing profile or error:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const saveField = async (name, value) => {
    try {
      await axios.post(
        `${API_URL}/coaches/profile`,
        { [name]: value },
        getAxiosConfig()
      );
      setMessage('Saved');
      setMessageType('');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Auto-save error:', error);
      setMessage('Error saving');
      setMessageType('error');
      setTimeout(() => setMessage(''), 4000);
    }
  };

  // College box: the shared CollegePicker owns search, ranking, keyboard and the popup.
  // Typing only updates the box; a PICK saves; free text is saved once, on leaving.
  const handleCollegeSelect = async (college) => {
    const logoUrl = college.l || null;
    const updated = {
      ...profile,
      unitid: college.u,
      school_name: college.n,
      state: college.s,
      school_logo: logoUrl
    };
    setProfile(updated);
    setCollegeQuery(college.n);

    try {
      await axios.post(`${API_URL}/coaches/profile`, {
        unitid: college.u,
        school_name: college.n,
        state: college.s,
      }, getAxiosConfig());
      setMessage('Saved');
      setMessageType('');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Auto-save error:', error);
      setMessage('Error saving');
      setMessageType('error');
      setTimeout(() => setMessage(''), 4000);
    }
  };

  const handleCollegeCommitText = async (text) => {
    if (text === profile.school_name) return;
    // Look up logo from loaded JSON data
    const match = collegesData.find(c => c.n === text);
    const logoUrl = match?.l || null;
    // Free text replaces any earlier pick, so its college id goes too (an
    // explicit null: matching prefers the stored unitid over the name).
    setProfile(prev => ({ ...prev, school_name: text, unitid: null, school_logo: logoUrl }));
    try {
      await axios.post(`${API_URL}/coaches/profile`, { school_name: text, unitid: null }, getAxiosConfig());
      setMessage('Saved'); setMessageType(''); setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Auto-save error:', err); setMessage('Error saving'); setMessageType('error'); setTimeout(() => setMessage(''), 4000);
    }
  };

  const handleBlur = async (e) => {
    const { name } = e.target;
    await saveField(name, profile[name]);
    // Auto-populate school logo when school_name changes
    if (name === 'school_name' && profile.school_name && profile.state) {
      try {
        const res = await axios.get(
          `${API_URL}/athletes/school-logo?school_name=${encodeURIComponent(profile.school_name)}&state=${encodeURIComponent(profile.state)}`,
          getAxiosConfig()
        );
        if (res.data.logo_url) {
          setProfile(prev => ({ ...prev, school_logo: res.data.logo_url }));
        }
      } catch (e) { /* ignore */ }
    }
  };

  const handleSchoolLogoUpload = async () => {
    if (!profile.school_name || !profile.state) {
      setMessage('Please set your school and state before uploading a logo');
      setMessageType('error');
      setTimeout(() => setMessage(''), 4000);
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`${API_URL}/coaches/upload-school-logo`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        setProfile(prev => ({ ...prev, school_logo: res.data.filepath }));
        setMessage('Logo uploaded');
        setMessageType('');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        setMessage(error.response?.data?.detail || 'Error uploading logo');
        setMessageType('error');
        setTimeout(() => setMessage(''), 4000);
      }
    };
    input.click();
  };

  return (
    <CoachProfileShell activeTab="Academics">
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">{message}</div>
      )}

      {/* School / Coaching Information */}
      <div className="settings-section">
        <h2 className="settings-section__title">Coaching Information</h2>
        <p className="settings-section__desc">Your school and coaching role details.</p>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
          <div className="form-field" style={{ flex: 1, position: 'relative' }}>
            <label className="form-field__label" htmlFor="school_name">School / Organization</label>
            <CollegePicker
              inputId="school_name"
              value={collegeQuery}
              collegesData={collegesData}
              onInputChange={setCollegeQuery}
              onSelect={handleCollegeSelect}
              onCommitText={handleCollegeCommitText}
              placeholder="Start typing school name..."
              ariaLabel="School / Organization"
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {profile.school_logo ? (
              <img
                src={profile.school_logo}
                alt="School Logo"
                style={{ width: '72px', height: '72px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: 'pointer' }}
                onClick={handleSchoolLogoUpload}
                title="Click to change logo"
              />
            ) : (
              <div
                style={{
                  width: '72px', height: '72px', borderRadius: '8px',
                  border: '2px dashed #cbd5e1', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#94a3b8', fontSize: '11px', textAlign: 'center',
                  cursor: 'pointer'
                }}
                onClick={handleSchoolLogoUpload}
                title="Upload school logo"
              >
                Logo
              </div>
            )}
            <Button variant="secondary" size="sm" onClick={handleSchoolLogoUpload}>
              {profile.school_logo ? 'Change' : 'Upload'}
            </Button>
            <span style={{ fontSize: '10px', color: '#94a3b8', textAlign: 'center', lineHeight: 1.2 }}>Shared for all at<br/>this school</span>
          </div>
        </div>
        <div className="form-field">
          <label className="form-field__label" htmlFor="position_title">Position / Title</label>
          <input
            id="position_title"
            type="text"
            name="position_title"
            value={profile.position_title}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-field__input"
            placeholder="Head Coach, Assistant Coach, Recruiting Coordinator..."
          />
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label" htmlFor="sport">Sport</label>
            <select
              id="sport"
              name="sport"
              value={profile.sport}
              onChange={(e) => { handleChange(e); saveField('sport', e.target.value); }}
              className="form-field__select"
            >
              {COACH_SPORTS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="division">Division</label>
            <select
              id="division"
              name="division"
              value={profile.division}
              onChange={handleChange}
              onBlur={handleBlur}
              className="form-field__select"
            >
              <option value="">Select...</option>
              <option value="NCAA D1 FBS">NCAA D1 FBS</option>
              <option value="NCAA D1 FCS">NCAA D1 FCS</option>
              <option value="NCAA D2">NCAA D2</option>
              <option value="NCAA D3">NCAA D3</option>
              <option value="NAIA">NAIA</option>
              <option value="JUCO">JUCO</option>
              <option value="High School">High School</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
      </div>
    </CoachProfileShell>
  );
}
